import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import getLocale from 'helpers/getLocale';

const DEFAULT_LANGUAGE = 'en';

// Dynamic import for translation files
const translationModules = {
  // Core
  en: () => import('./translation/en.json'),
  de: () => import('./translation/de.json'),
  fr: () => import('./translation/fr.json'),
  it: () => import('./translation/it.json'),
  es: () => import('./translation/es.json'),
  pt: () => import('./translation/pt.json'),

  // Central & Eastern Europe
  ua: () => import('./translation/ua.json'),
  ru: () => import('./translation/ru.json'),
  pl: () => import('./translation/pl.json'),
  ro: () => import('./translation/ro.json'),
  cs: () => import('./translation/cs.json'),
  sk: () => import('./translation/sk.json'),
  hu: () => import('./translation/hu.json'),
  bg: () => import('./translation/bg.json'),

  // Nordic
  fi: () => import('./translation/fi.json'),
  sv: () => import('./translation/sv.json'),
  da: () => import('./translation/da.json'),
  no: () => import('./translation/no.json'),
  is: () => import('./translation/is.json'),

  // Baltic
  lt: () => import('./translation/lt.json'),
  lv: () => import('./translation/lv.json'),
  et: () => import('./translation/et.json'),

  // Balkans
  sr: () => import('./translation/sr.json'),
  hr: () => import('./translation/hr.json'),
  mk: () => import('./translation/mk.json'),
  sq: () => import('./translation/sq.json'),
  sl: () => import('./translation/sl.json'),

  // Other significant European
  el: () => import('./translation/el.json'),
  tr: () => import('./translation/tr.json'),
  nl: () => import('./translation/nl.json'),
  ca: () => import('./translation/ca.json'),

  // Caucasus
  ka: () => import('./translation/ka.json'),
  hy: () => import('./translation/hy.json')
};

// Get the current language from URL/locale
const currentLanguage = getLocale() || DEFAULT_LANGUAGE;

// Prerender pre-bakes the active-locale JSON into the HTML as a non-executed
// <script type="application/json" id="__i18n__" data-lang="xx">…</script>
// block. Reading it synchronously here lets initI18n resolve without the
// dynamic-import round trip that previously kept React from mounting for
// ~250 ms after FCP. Falls through gracefully when the block is absent
// (HMR, runtime locale switches via setLanguage, JSDOM tests).
const readInlineTranslation = () => {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById('__i18n__');
  if (!el || !el.textContent) return null;
  try {
    return {
      lang: el.dataset.lang || currentLanguage,
      data: JSON.parse(el.textContent)
    };
  } catch {
    return null;
  }
};

// Load translation for a specific language
const loadTranslation = async (lang) => {
  const loader = translationModules[lang] || translationModules[DEFAULT_LANGUAGE];
  const module = await loader();
  return module.default || module;
};

// Switch the active language at runtime: lazy-load its bundle if needed,
// register it with i18next, and activate it. Used by in-app language pickers
// (e.g. the Assessment dropdown) where a full reload is undesirable because
// it would lose ephemeral session state.
export const setLanguage = async (lang) => {
  if (!translationModules[lang]) return false;
  if (!i18n.hasResourceBundle(lang, 'translation')) {
    const translation = await loadTranslation(lang);
    i18n.addResourceBundle(lang, 'translation', translation, true, true);
  }
  await i18n.changeLanguage(lang);
  return true;
};

// Initialize i18n
const initI18n = async () => {
  const inline = readInlineTranslation();
  const lang = inline?.lang || currentLanguage;
  const translation = inline?.data || (await loadTranslation(lang));

  await i18n.use(initReactI18next).init({
    resources: {
      [lang]: {
        translation
      }
    },
    lng: lang,
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false // React already escapes
    },
    react: {
      useSuspense: false
    }
  });

  return i18n;
};

// Export the initialization promise
export const i18nPromise = initI18n();

export default i18n;

